import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Brain, Rocket, TrendingUp, Blocks, BarChart3, Cpu } from 'lucide-react';

const ExpertiseIcons = [Brain, Rocket, TrendingUp, Blocks, BarChart3, Cpu];

export function ExpertiseSection() {
  const { t, currentLanguage } = useLanguage();
  const items = t('expertise.items') || [];

  const sectionLabel = {
    en: 'Capabilities',
    tr: 'Yetenekler',
    de: 'Fähigkeiten'
  };

  // First item is featured, rest are in compact list
  const featuredItem = items[0];
  const otherItems = items.slice(1);

  return (
    <section
      id="expertise"
      data-testid="expertise-section"
      className="relative py-20 md:py-28"
      style={{ background: 'var(--bg-light-section)' }}
    >
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
          <div>
            <p 
              className="text-xs uppercase tracking-widest mb-4"
              style={{ color: '#0F766E' }}
            >
              {sectionLabel[currentLanguage] || sectionLabel.en}
            </p>
            <h2 
              className="text-3xl md:text-4xl font-semibold tracking-tight"
              data-testid="expertise-title"
              style={{ color: 'var(--text-on-light-primary)' }}
            >
              {t('expertise.title')}
            </h2>
          </div>
          <p 
            className="text-base max-w-md"
            style={{ color: 'var(--text-on-light-muted)' }}
          >
            {t('expertise.subtitle')}
          </p>
        </div>

        {/* Two-column layout: Featured + List */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Left: Featured Item - Large card */}
          {featuredItem && (
            <div 
              className="p-8 rounded-2xl h-full flex flex-col justify-between"
              style={{ 
                background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.08) 0%, rgba(15, 118, 110, 0.02) 100%)',
                border: '1px solid rgba(15, 118, 110, 0.15)'
              }}
            >
              <div>
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                  style={{ 
                    background: 'rgba(15, 118, 110, 0.1)',
                    border: '1px solid rgba(15, 118, 110, 0.2)'
                  }}
                >
                  <Brain 
                    className="w-7 h-7" 
                    style={{ color: '#0F766E' }}
                    strokeWidth={1.5}
                  />
                </div>
                <h3 
                  className="text-2xl font-semibold mb-4"
                  style={{ color: 'var(--text-on-light-primary)' }}
                >
                  {featuredItem.title}
                </h3>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {featuredItem.description}
                </p>

                {/* Prediction Visualization */}
                <div className="mt-6">
                  <svg viewBox="0 0 240 100" className="w-full" style={{ maxWidth: '300px' }}>
                    <defs>
                      <linearGradient id="predictionGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(15, 118, 110, 0.2)" />
                        <stop offset="100%" stopColor="rgba(15, 118, 110, 0)" />
                      </linearGradient>
                      <linearGradient id="futureGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(15, 118, 110, 0.1)" />
                        <stop offset="100%" stopColor="rgba(15, 118, 110, 0)" />
                      </linearGradient>
                    </defs>
                    
                    {/* Background area */}
                    <rect x="0" y="0" width="240" height="100" fill="rgba(15, 118, 110, 0.03)" rx="6" />
                    
                    {/* Grid lines */}
                    <g stroke="rgba(15, 118, 110, 0.1)" strokeWidth="0.5">
                      <line x1="20" y1="20" x2="220" y2="20" />
                      <line x1="20" y1="40" x2="220" y2="40" />
                      <line x1="20" y1="60" x2="220" y2="60" />
                      <line x1="20" y1="80" x2="220" y2="80" />
                    </g>
                    
                    {/* Historical area fill */}
                    <path 
                      d="M 25 70 L 45 55 L 65 62 L 85 48 L 105 52 L 125 38 L 140 42 L 140 85 L 25 85 Z" 
                      fill="url(#predictionGradient)" 
                    />
                    
                    {/* Prediction area fill (dashed region) */}
                    <path 
                      d="M 140 42 L 165 32 L 190 28 L 215 22 L 215 85 L 140 85 Z" 
                      fill="url(#futureGradient)" 
                    />
                    
                    {/* Confidence band */}
                    <path 
                      d="M 140 42 Q 170 45 190 38 L 215 35 L 215 15 L 190 20 Q 170 22 140 42" 
                      fill="rgba(15, 118, 110, 0.05)" 
                    />
                    
                    {/* Historical data line */}
                    <path 
                      d="M 25 70 L 45 55 L 65 62 L 85 48 L 105 52 L 125 38 L 140 42" 
                      fill="none" 
                      stroke="#0F766E" 
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    
                    {/* Prediction line (dashed) */}
                    <path 
                      d="M 140 42 L 165 32 L 190 28 L 215 22" 
                      fill="none" 
                      stroke="#0F766E" 
                      strokeWidth="2"
                      strokeDasharray="4,3"
                      strokeLinecap="round"
                    />
                    
                    {/* Data points - historical */}
                    <circle cx="45" cy="55" r="3" fill="#FFFFFF" stroke="#0F766E" strokeWidth="1.5" />
                    <circle cx="85" cy="48" r="3" fill="#FFFFFF" stroke="#0F766E" strokeWidth="1.5" />
                    <circle cx="125" cy="38" r="3" fill="#FFFFFF" stroke="#0F766E" strokeWidth="1.5" />
                    
                    {/* Current point (larger) */}
                    <circle cx="140" cy="42" r="4" fill="#1C7C7D" stroke="#1C7C7D" strokeWidth="2" />
                    
                    {/* Prediction point */}
                    <circle cx="190" cy="28" r="3" fill="none" stroke="rgba(28, 124, 125, 0.6)" strokeWidth="1.5" strokeDasharray="2,2" />
                    
                    {/* Divider line - NOW */}
                    <line x1="140" y1="12" x2="140" y2="88" stroke="rgba(28, 124, 125, 0.3)" strokeWidth="1" strokeDasharray="3,3" />
                    
                    {/* Labels */}
                    <text x="80" y="95" textAnchor="middle" fill="rgba(107, 119, 133, 0.5)" fontSize="7" fontFamily="'JetBrains Mono', monospace">historical</text>
                    <text x="178" y="95" textAnchor="middle" fill="rgba(28, 124, 125, 0.5)" fontSize="7" fontFamily="'JetBrains Mono', monospace">prediction</text>
                    
                    {/* Accuracy badge */}
                    <rect x="185" y="50" width="45" height="16" rx="3" fill="rgba(15, 61, 62, 0.5)" />
                    <text x="207" y="61" textAnchor="middle" fill="#1C7C7D" fontSize="8" fontFamily="'JetBrains Mono', monospace">+12.4%</text>
                  </svg>
                </div>
              </div>
              <div 
                className="mt-8 pt-6 border-t flex items-center gap-2"
                style={{ borderColor: 'rgba(28, 124, 125, 0.15)' }}
              >
                <span 
                  className="w-2 h-2 rounded-full"
                  style={{ background: '#2E7D32' }}
                />
                <span 
                  className="text-xs uppercase tracking-wider"
                  style={{ color: 'var(--text-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {currentLanguage === 'tr' ? 'Birincil Uzmanlık' : currentLanguage === 'de' ? 'Primäre Expertise' : 'Primary Expertise'}
                </span>
              </div>
            </div>
          )}

          {/* Right: Compact list of other items */}
          <div className="space-y-3">
            {otherItems.map((item, index) => {
              const IconComponent = ExpertiseIcons[index + 1] || Brain;
              const realIndex = index + 1;
              
              return (
                <div
                  key={index}
                  data-testid={`expertise-card-${realIndex}`}
                  className="group flex items-start gap-4 p-4 rounded-xl transition-all duration-300 hover:bg-[rgba(15,118,110,0.08)]"
                  style={{ 
                    background: '#F8FAFB',
                    border: '1px solid rgba(15, 118, 110, 0.1)'
                  }}
                >
                  {/* Number */}
                  <span 
                    className="text-xs font-medium pt-1 w-6 flex-shrink-0"
                    style={{ 
                      color: '#0F766E',
                      fontFamily: 'JetBrains Mono, monospace'
                    }}
                  >
                    0{realIndex + 1}
                  </span>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 
                      className="text-base font-medium mb-1"
                      style={{ color: 'var(--text-on-light-primary)' }}
                    >
                      {item.title}
                    </h3>
                    <p 
                      className="text-sm leading-relaxed"
                      style={{ color: 'var(--text-on-light-muted)' }}
                    >
                      {item.description}
                    </p>
                  </div>

                  {/* Icon */}
                  <div 
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(15, 118, 110, 0.1)' }}
                  >
                    <IconComponent 
                      className="w-4 h-4" 
                      style={{ color: '#0F766E' }}
                      strokeWidth={1.5}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
